import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import CustomTextInput from "../components/textInput";
import ErrorManagement, { addErrorToClass } from "../components/errormanagement";
import CustomButton from "../components/button";
import EmployeeSettingsPassword from "./EmployeeSettingsPassword";
import { useContext, useEffect, useState } from "react";
import { UserDataContext } from "../context/UserContextProvider";
import { ToastDataContext } from "../context/ToastContextProvider";
import { hrSettingsBaseEndpoint } from "../hr_endpoints";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomSelect from "../components/select";
import SettingsExtraInfoSala from "./SettingsExtraInfoSala";

export default function HRSettings({ }) {

    const { t } = useTranslation();

    const schema = yup.object({
        first_name: yup.string().required(t('settings.firstNameRequired')),
        last_name: yup.string().required(t('settings.lastNameRequired')),
        email: yup.string().email(t('settings.emailInvalid')).required(t('settings.emailRequired')),
        phone: yup.string().optional().nullable()
    })

    const { register, reset, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    });

    const userContext = useContext(UserDataContext)
    const toastContext = useContext(ToastDataContext)
    const navigate = useNavigate()

    useEffect(() => {
        reset({
            first_name: userContext.user?.firstName,
            last_name: userContext.user?.lastName,
            email: userContext.user?.email,
            phone: userContext.user?.phone,
            lang: userContext.user?.lang
        })
    }, [])

    const onSubmit = async (data) => {
        const response = await fetch(hrSettingsBaseEndpoint, {
            method: "PUT",
            body: JSON.stringify({
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
                phone: data.phone,
                lang: lang
            }),
            headers: {
                Authorization: `Bearer ${userContext.user?.token}`,
                "Content-Type": "application/json",
            },
        });

        if (response.status === 401) {
            userContext.setUserData({})
            navigate('/login')
            toastContext.error(t('sessionExpired'));
        }

        if (!response.ok) {
            const error = await response.text();
            // The backend answers with a stable code, not a sentence, so
            // each interface can phrase it in its own language.
            toastContext.error(error.includes("PHONE_NUMBER_IN_USE")
                ? t('settings.phoneNumberInUse')
                : error);
        } else {
            userContext.setUserData({
                ...userContext.user,
                firstName: data.first_name,
                lastName: data.last_name,
                email: data.email,
                phone: data.phone,
                lang: lang
            });
            toastContext.success(t('settings.updateSuccess'));
        }
    }

    const langOptions = [
        { id: 'ro', name: t('settings.ro') },
        { id: 'en', name: t('settings.en') },
    ]

    const [lang, setLang] = useState(userContext.user?.lang || 'ro');

    return (
        <div className="grid grid-cols-6">
            <div className="col-span-1" />
            <div className="col-span-4 ml-2">
                <form onSubmit={handleSubmit(onSubmit)} className="pb-6">
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-row gap-2 flex-wrap">
                            <div>
                                <label htmlFor="first_name" className="block font-subtitle text-neutral-1100 mb-2">{t('settings.firstName')}</label>
                                <CustomTextInput register={register('first_name')} placeholder={t('settings.firstName')} className={addErrorToClass(errors.first_name)} />
                                <ErrorManagement errorObj={errors.first_name} />
                            </div>
                            <div>
                                <label htmlFor="last_name" className="block font-subtitle text-neutral-1100 mb-2">{t('settings.lastName')}</label>
                                <CustomTextInput register={register('last_name')} placeholder={t('settings.lastName')} className={addErrorToClass(errors.last_name)} />
                                <ErrorManagement errorObj={errors.last_name} />
                            </div>
                            <div>
                                <label htmlFor="email" className="block font-subtitle text-neutral-1100 mb-2">{t('settings.email')}</label>
                                <CustomTextInput type="email" register={register('email')} placeholder={t('settings.email')} className={addErrorToClass(errors.email)} />
                                <ErrorManagement errorObj={errors.email} />
                            </div>
                            <div>
                                <label htmlFor="phone" className="block font-subtitle text-neutral-1100 mb-2">{t('settings.phone')}</label>
                                <CustomTextInput type="text" register={register('phone')} placeholder={t('settings.phone')} className={addErrorToClass(errors.phone)} />
                                <ErrorManagement errorObj={errors.phone} />
                            </div>
                            <div>
                                <label htmlFor="lang" className="block font-subtitle text-neutral-1100 mb-2">{t('settings.lang')}</label>
                                <CustomSelect options={langOptions} onChange={(e) => setLang(e.target.value)} value={lang} />
                                <ErrorManagement errorObj={errors.lang} />
                            </div>
                        </div>
                        <CustomButton className={"max-w-[10rem]"} type="submit">{t('settings.save')}</CustomButton>
                    </div>
                </form>
                <EmployeeSettingsPassword />
                <SettingsExtraInfoSala />
            </div>
        </div>
    )
}